import {
  importProvidersFrom,
  inject,
  provideEnvironmentInitializer,
} from '@angular/core';
import { ApplicationStore, getModuleGroupById, SOFTLINE_FEATURE_APPLICATIONS } from '@softline/application';
import {
  SOFTLINE_SERVICE_HTTP,
  SOFTLINE_STORE_ENTITY_SERVICE,
  SoftlineEntityDetailService,
  SoftlineEntityService,
  Store,
  StoreModule,
} from '@softline/core';
import { SOFTLINE_FEATURE_TRANSLATION, TranslationStore } from '@softline/ui-core';
import {
  provideSoftAppsModule,
  withLazySoftAppsModuleTranslation,
  withSoftAppsModuleInfo,
  withSoftAppsModuleRoutes,
} from '@softapps/core';
import { default as de } from '../i18n/de.json';
import { provideArtikelvorratGlobals } from './artikelvorrat.globals';
import { artikelvorratRoutes } from './artikelvorrat.routes';
import {
  SOFTLINE_API_MDE_ARTIKELVORRAT_ARTIKEL,
  SOFTLINE_API_MDE_ARTIKELVORRAT_BEWEGUNGEN,
  SOFTLINE_API_MDE_ARTIKELVORRAT_KOPF_ENTITES,
} from './artikelvorrat.api';
import {
  SOFTLINE_FEATURE_MDE_ARTIKELVORRAT,
  SOFTLINE_FEATURE_MDE_ARTIKELVORRAT_ARTIKEL_DETAILS,
  SOFTLINE_FEATURE_MDE_ARTIKELVORRAT_BEWE,
  SOFTLINE_FEATURE_MDE_ARTIKELVORRAT_OFFENE_KOEPFE,
} from './artikelvorrat.shared';
import { ArtikelvorratResolver } from './resolver/artikelvorrat.resolver';
import { ArtikelvorratService } from './services/artikelvorrat.service';
import { ArtikelvorratArtikelDetailStore } from './store/artikelvorrat-artikel-detail.store';
import { ArtikelvorratBeweStore } from './store/artikelvorrat-bewe.store';
import { ArtikelvorratOffeneKoepfeStore } from './store/artikelvorrat-offene-koepfe.store';
import { ArtikelvorratStore } from './store/artikelvorrat.store';

export const artikelvorratProviders = [
  provideEnvironmentInitializer(async () => {
    const store = inject(Store);
    store.commit(SOFTLINE_FEATURE_TRANSLATION, TranslationStore.mutations.add, {
      module: 'mdeArtikelvorrat',
      language: 'de',
      translations: de,
    });

    store.commit(SOFTLINE_FEATURE_APPLICATIONS, ApplicationStore.mutations.add, {
      name: 'Artikelvorrat',
      icon: 'fa-regular fa-boxes-stacked',
      route: '/mde-artikelvorrat',
      permission: 'ARTIKELVORRAT',
      group: getModuleGroupById(6)
    });

    store.commit(
      SOFTLINE_FEATURE_MDE_ARTIKELVORRAT_BEWE,
      ArtikelvorratBeweStore.mutations.setSort,
      {
        property: 'id',
        title: 'Erfassungszeitpunkt',
        direction: 'desc',
      } as any
    );
  }),
  importProvidersFrom(
    StoreModule.forFeature({
      name: SOFTLINE_FEATURE_MDE_ARTIKELVORRAT,
      feature: ArtikelvorratStore.feature,
      providers: [
        {
          provide: ArtikelvorratService,
          useClass: ArtikelvorratService,
        },
      ],
    }),
    StoreModule.forFeature({
      name: SOFTLINE_FEATURE_MDE_ARTIKELVORRAT_BEWE,
      feature: ArtikelvorratBeweStore.feature,
      providers: [
        {
          provide: SOFTLINE_STORE_ENTITY_SERVICE,
          useFactory: () =>
            new SoftlineEntityService(
              inject(SOFTLINE_SERVICE_HTTP),
              SOFTLINE_API_MDE_ARTIKELVORRAT_BEWEGUNGEN
            ),
        },
      ],
    }),
    StoreModule.forFeature({
      name: SOFTLINE_FEATURE_MDE_ARTIKELVORRAT_ARTIKEL_DETAILS,
      feature: ArtikelvorratArtikelDetailStore.feature,
      providers: [
        {
          provide: SOFTLINE_STORE_ENTITY_SERVICE,
          useFactory: () => {
            return new SoftlineEntityDetailService(
              inject(SOFTLINE_SERVICE_HTTP),
              SOFTLINE_API_MDE_ARTIKELVORRAT_ARTIKEL,
              'detail'
            );
          },
        },
      ],
    }),
    StoreModule.forFeature({
      name: SOFTLINE_FEATURE_MDE_ARTIKELVORRAT_OFFENE_KOEPFE,
      feature: ArtikelvorratOffeneKoepfeStore.feature,
      providers: [
        {
          provide: SOFTLINE_STORE_ENTITY_SERVICE,
          useFactory: () =>
            new SoftlineEntityService(
              inject(SOFTLINE_SERVICE_HTTP),
              SOFTLINE_API_MDE_ARTIKELVORRAT_KOPF_ENTITES
            ),
        },
      ],
    })
  ),
  ArtikelvorratResolver,
];

export function provideArtikelvorratModule() {
  return [
    provideSoftAppsModule(
      {
        name: 'mdeArtikelvorrat',
        title: 'Artikelvorrat',
        icon: 'fa-regular fa-boxes-stacked',
        route: '/mde-artikelvorrat',
        permission: 'ARTIKELVORRAT',
        group: getModuleGroupById(6),
      },
      withSoftAppsModuleRoutes({ app: artikelvorratRoutes }),
      withLazySoftAppsModuleTranslation({
        lazy: { de: () => import('../i18n/de.json') },
      }),
      withSoftAppsModuleInfo({
        library: '@softapps/mde/artikelvorrat',
        remoteConfig: 'mde-artikelvorrat',
        description: 'Artikelvorrat mit dem MDE-Gerät erfassen.',
      })
    ),
    ...provideArtikelvorratGlobals(),
  ];
}
